import React, { useState, useEffect, useRef, useCallback } from 'react';
import { dariWords } from './dariWords';
import {
  saveRecording,
  deleteRecording,
  listRecordedWords,
  audioUrlFor,
  invalidateAudioCache,
} from './audioStorage';

function AdminPanel() {
  const [recordedSet, setRecordedSet] = useState(new Set());
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [pendingBlob, setPendingBlob] = useState(null);
  const [pendingUrl, setPendingUrl] = useState(null);
  const [savedVersion, setSavedVersion] = useState(0);
  const [error, setError] = useState(null);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const didInitialJump = useRef(false);

  const currentWord = dariWords[currentIndex];

  const refreshRecorded = () => {
    listRecordedWords(dariWords.map((w) => w.dari))
      .then((set) => {
        setRecordedSet(set);
        // On first load, jump to the first word that still needs a recording.
        if (!didInitialJump.current) {
          didInitialJump.current = true;
          const firstUnsaved = dariWords.findIndex((w) => !set.has(w.dari));
          if (firstUnsaved > 0) setCurrentIndex(firstUnsaved);
        }
      })
      .catch(() => {});
  };

  useEffect(() => { refreshRecorded(); }, []);

  // Reset transient state when navigating to a new word
  useEffect(() => {
    setPendingBlob(null);
    setPendingUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
    setError(null);
  }, [currentIndex]);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const startRecording = useCallback(async () => {
    if (isRecording) return;
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || 'audio/webm' });
        const url = URL.createObjectURL(blob);
        setPendingBlob(blob);
        setPendingUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return url; });
        stopStream();
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setIsRecording(true);
    } catch (err) {
      setError(err.message || 'Microphone access denied');
      stopStream();
    }
  }, [isRecording]);

  const stopRecording = useCallback(() => {
    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== 'inactive') {
      mr.stop();
    }
    setIsRecording(false);
  }, []);

  useEffect(() => () => { stopStream(); }, []);

  const handleSave = async () => {
    if (!pendingBlob) return;
    try {
      await saveRecording(currentWord.dari, pendingBlob);
      invalidateAudioCache(currentWord.dari);
      setRecordedSet((s) => new Set(s).add(currentWord.dari));
      setSavedVersion((v) => v + 1);
      if (pendingUrl) URL.revokeObjectURL(pendingUrl);
      setPendingUrl(null);
      setPendingBlob(null);
    } catch (err) {
      setError(err.message || 'Save failed');
    }
  };

  const handleSaveAndNext = async () => {
    await handleSave();
    if (currentIndex < dariWords.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handleRetry = () => {
    if (pendingUrl) URL.revokeObjectURL(pendingUrl);
    setPendingUrl(null);
    setPendingBlob(null);
  };

  const handleDelete = async () => {
    try {
      await deleteRecording(currentWord.dari);
      invalidateAudioCache(currentWord.dari);
      setRecordedSet((s) => {
        const next = new Set(s);
        next.delete(currentWord.dari);
        return next;
      });
      setSavedVersion((v) => v + 1);
    } catch (err) {
      setError(err.message || 'Delete failed');
    }
  };

  const hasSaved = recordedSet.has(currentWord.dari);
  const savedSrc = hasSaved ? `${audioUrlFor(currentWord.dari)}?v=${savedVersion}` : null;

  const recordedCount = recordedSet.size;

  return (
    <div className="admin-panel">
      <h2>Admin · Record Word Audio</h2>
      <p className="admin-progress">
        {recordedCount} / {dariWords.length} words recorded
      </p>

      <div className="admin-current text-container">
        <div className="dariWordDisplay">
          <span className="dariWordText" lang="fa" dir="rtl">{currentWord.dari}</span>
        </div>
        <div className="englishWordDisplay">{currentWord.english}</div>

        <button
          className={`record-btn ${isRecording ? 'recording' : ''}`}
          onMouseDown={startRecording}
          onMouseUp={stopRecording}
          onMouseLeave={() => { if (isRecording) stopRecording(); }}
          onTouchStart={(e) => { e.preventDefault(); startRecording(); }}
          onTouchEnd={(e) => { e.preventDefault(); stopRecording(); }}
        >
          {isRecording ? '● Recording… release to stop' : '🎙 Hold to record'}
        </button>

        {error && <p className="admin-error">{error}</p>}

        {pendingUrl && (
          <div className="admin-pending">
            <p>Preview new recording:</p>
            <audio src={pendingUrl} controls />
            <div className="admin-actions">
              <button onClick={handleRetry}>Retry</button>
              <button onClick={handleSave}>Save</button>
              <button onClick={handleSaveAndNext} className="primary">
                Save & Next →
              </button>
            </div>
          </div>
        )}

        {!pendingUrl && savedSrc && (
          <div className="admin-saved">
            <p>Saved recording:</p>
            <audio src={savedSrc} controls />
            <div className="admin-actions">
              <button onClick={handleDelete}>Delete</button>
            </div>
          </div>
        )}

        <div className="admin-nav">
          <button
            onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))}
            disabled={currentIndex === 0}
          >
            ← Prev
          </button>
          <span>{currentIndex + 1} / {dariWords.length}</span>
          <button
            onClick={() => setCurrentIndex(Math.min(dariWords.length - 1, currentIndex + 1))}
            disabled={currentIndex === dariWords.length - 1}
          >
            Next →
          </button>
        </div>
      </div>

      <div className="admin-index text-container">
        <h3>All words</h3>
        <ul className="word-index">
          {dariWords.map((w, i) => {
            const has = recordedSet.has(w.dari);
            return (
              <li
                key={w.dari}
                className={`word-row ${i === currentIndex ? 'active' : ''}`}
                onClick={() => setCurrentIndex(i)}
              >
                <span className="word-status">{has ? '✓' : '○'}</span>
                <span className="word-dari" lang="fa" dir="rtl">{w.dari}</span>
                <span className="word-english">{w.english}</span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

export default AdminPanel;
